import { auth } from "@/services/auth"
import { logger } from "@/services/logger"
import { useSubscriptionStore } from "@/store/subscription"
import { isEntitled } from "@/services/entitlements"

// Route names reachable while a tenant is locked out (billing/paywall + all unauthenticated pages).
export const BILLING_ALLOWLIST = [
  "Checkout",
  "CheckoutReturn",
  "Plans",
  "Login",
  "Registration",
  "ForgotPassword",
  "ResetPassword",
  "SuccessPassword",
  "NotFound",
]

export const initializeNavigationGuards = router => {
  authorityGuard(router)
  subscriptionGuard(router)
  featureSetGuard(router)
}

function authorityGuard(router) {
  router.beforeEach(to => {
    // this route can only be accessed by unauthenticated users
    // check if user is logged in
    // if so, redirect to dashboard
    if (to.meta.requiresAuth === false && auth.isLoggedIn()) {
      logger.log(
        `Redirect to Dashboard because user tries to access page which is only there for unauthenticated users ${to.path}`
      )
      return { name: "Dashboard" }
    }

    // this route can only be accessed by authenticated users
    // check if user is logged in
    // if not, redirect to login page
    if (to.meta.requiresAuth && !auth.isLoggedIn()) {
      logger.log("Redirect to Login page because user is not logged in but requires auth")
      return { name: "Login" }
    }

    // this route can only be accessed by authorized users
    // check if user is logged in and has at least one authorization
    // if not, redirect to dashboard page todo: clarify
    if (to.meta.requiresPermissions && auth.isLoggedIn()) {
      if (!auth.hasOneOfPermissions(to.meta.requiresPermissions)) {
        logger.log("Redirect to Dashboard because user has no permissions")
        return { name: "Dashboard" }
      }
    }

    // this route can only be accessed by authorized users
    // check if user is logged in and has all authorizations
    // if not, redirect to dashboard page todo: clarify
    if (to.meta.requiresAllPermissions && auth.isLoggedIn()) {
      if (!auth.hasAllOfPermissions(to.meta.requiresAllPermissions)) {
        logger.log("Redirect to Dashboard because user has no permissions")
        return { name: "Dashboard" }
      }
    }

    return true
  })
}

// Locks out tenants without an active/grace/exempt subscription, routing them to the paywall.
function subscriptionGuard(router) {
  router.beforeEach(to => {
    if (!auth.isLoggedIn()) return true

    const subscription = useSubscriptionStore()
    // Never act before status has been loaded, or we would bounce a valid tenant.
    if (!subscription.loaded) return true

    if (!subscription.hasAccess && !BILLING_ALLOWLIST.includes(to.name)) {
      logger.log("Redirect to Checkout because tenant has no active subscription")
      return { name: "Checkout" }
    }

    return true
  })
}

// Blocks routes whose meta.requiresFeatureSet the tenant has not bought, sending them to the add-on
// upsell rather than the dashboard: these feature-sets are purchasable, so "you can't go there" is only
// half the answer — FeatureLockedView names the feature and offers the way to unlock it. A tenant whose
// subscription is inactive altogether never gets here; subscriptionGuard runs first and routes to the
// paywall, which is the right order (activate the subscription, then extend it).
function featureSetGuard(router) {
  router.beforeEach(to => {
    if (!to.meta.requiresFeatureSet || !auth.isLoggedIn()) return true

    if (!isEntitled(to.meta.requiresFeatureSet)) {
      logger.log("Redirect to FeatureLocked because tenant is not entitled to the required feature-set")
      return { name: "FeatureLocked", params: { featureSet: to.meta.requiresFeatureSet } }
    }

    return true
  })
}
